import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Form, Row, Skeleton } from 'antd'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'src/store'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'

import ButtonCustom from 'src/components/form/Button'
import { getMenus, getMenusByLp } from 'src/useCase/question'

import {
  FormValues,
  useMedicalSpecialtyForm,
} from 'src/containers/MedicalSpecialty/components/useMedicalSpecialtyForm'
import SelectField from 'src/components/form/SelectField'
import LocalStorage from 'src/utils/LocalStorage'
import choicesUserAction from 'src/store/domain/choicesUser/details'
import GenderSelection from 'src/components/form/GenderSelection'
import {
  getStoredSelectedGender,
  hasEffectiveGender,
  hasUserProfileGender,
  setStoredSelectedGender,
  shouldOfferGenderSelection,
} from 'src/utils/gender'

const MedicalSpecialtyForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [savingGender, setSavingGender] = useState(false)

  const { form, onSubmit, updateGenderProfile } = useMedicalSpecialtyForm()
  const { control, handleSubmit, formState, setValue } = form
  const [searchParams] = useSearchParams()
  const clinicId = searchParams.get('clinicId')
  const authInfo = useAppSelector((state) => state.ui.app.authInfo)
  const userClinicId = authInfo?.clinics?.[0]?.id

  const isClinicMismatch = Boolean(
    authInfo &&
      clinicId &&
      userClinicId &&
      Number(clinicId) !== Number(userClinicId),
  )

  useEffect(() => {
    if (isClinicMismatch) {
      navigate(PATH_MY_PAGE.OTHER_MENU)
    }
  }, [isClinicMismatch, navigate])

  const accessToken = LocalStorage.accessToken
  const exists = searchParams.get('exists')
  const genderFromUrl =
    searchParams.get('gender') || searchParams.get('gender_id')
  const storedGender = getStoredSelectedGender()
  const [selectGender, setSelectGender] = useState<string>()

  const menus = useAppSelector(
    (state) => state.domain.automaticPlan.menus.entity,
  )
  const choicesOfUser = useAppSelector(
    (state) => state.domain.choicesUser.choicesUser,
  )

  const loadingMenu =
    useAppSelector((state) => state.domain.automaticPlan.menus.loading) || false
  const menusOptions =
    Object.assign(menus).length > 0 &&
    Object.assign(menus).reduce((result, currentValue) => {
      result = [
        ...result,
        {
          id: currentValue.id,
          value: currentValue.id,
          label: currentValue.name,
        },
      ]
      return result
    }, [])

  const { menuId } = location.state || {}

  const genderSources = useMemo(
    () => ({
      userGenderId: authInfo?.gender_id,
      genderFromUrl,
      selectedGenderFromStorage: storedGender,
      localSelection: selectGender,
    }),
    [authInfo?.gender_id, genderFromUrl, storedGender, selectGender],
  )

  const showGenderSelection = shouldOfferGenderSelection(genderSources)
  const canProceed = hasEffectiveGender(genderSources)
  const isMenuDisabled = !canProceed
  const genderDisplayValue = selectGender || storedGender || undefined

  const loadMenusForLoggedInUser = useCallback(() => {
    if (!accessToken) return
    dispatch(getMenus({ allow_menu_ids: menuId ? [menuId] : [] }))
  }, [accessToken, menuId, dispatch])

  const handleSetGender = useCallback(
    async (gender: number) => {
      const genderStr = String(gender)
      setSelectGender(genderStr)
      setStoredSelectedGender(genderStr)
      setValue('menu_id', '')
      setSavingGender(true)
      await updateGenderProfile(genderStr)
      setSavingGender(false)
    },
    [updateGenderProfile, setValue],
  )

  useEffect(() => {
    const prefilled = hasUserProfileGender(authInfo?.gender_id)
      ? String(authInfo?.gender_id ?? '')
      : storedGender
    if (prefilled && !selectGender) {
      setSelectGender(prefilled)
    }
  }, [authInfo?.gender_id, storedGender, selectGender])

  useEffect(() => {
    const isPaying = !!LocalStorage.getLocalStorage('is_paying')

    if (isPaying) {
      LocalStorage.removeLocalStorage('is_paying')
    }

    if (menuId) {
      setValue('menu_id', menuId)
    }
  }, [menuId, setValue])

  useEffect(() => {
    if (!selectGender) return

    if (choicesOfUser?.from === 'LP' && !accessToken) {
      if (LocalStorage.getUserTreatmentId()) {
        LocalStorage.removeUserTreatmentId()
      }
      if (choicesOfUser?.menuId && choicesOfUser?.planId && !exists) {
        setValue('menu_id', choicesOfUser.menuId)
        setValue('plan_id', choicesOfUser.planId)

        setLoadingSubmit(true)
        handleSubmit(onSubmit)().finally(() => {
          setLoadingSubmit(false)
          setValue('menu_id', '')
          setValue('plan_id', '')
          dispatch(
            choicesUserAction.setChoicesData({
              clinicId: '',
              menuId: '',
              planId: '',
              from: '',
            }),
          )
        })
        return
      }

      if (clinicId) {
        dispatch(
          getMenusByLp({
            clinicId,
            params: { target_gender: Number(selectGender) },
          }),
        )
      }
    }
  }, [
    selectGender,
    accessToken,
    choicesOfUser,
    exists,
    clinicId,
    dispatch,
    handleSubmit,
    onSubmit,
    setValue,
  ])

  useEffect(() => {
    if (accessToken && hasUserProfileGender(authInfo?.gender_id)) {
      loadMenusForLoggedInUser()
    }
  }, [accessToken, authInfo?.gender_id, loadMenusForLoggedInUser])

  return (
    <Wrapper>
      {showGenderSelection && (
        <Row>
          <Col xs={24}>
            <Row className="justify-content-between">
              <p>性別（どちらかを選択してください）</p>
              <p>
                <span className="request">必須</span>
              </p>
            </Row>
            <div className="mt-15 mb-30">
              <GenderSelection
                value={genderDisplayValue}
                onChange={(val) => handleSetGender(Number(val))}
              />
            </div>
          </Col>
        </Row>
      )}

      {loadingMenu || loadingSubmit || savingGender ? (
        <Skeleton />
      ) : (
        <Form onFinish={handleSubmit(onSubmit)}>
          <SelectField<FormValues>
            label="診療科目"
            selectProps={{
              options: menusOptions,
              loading: false,
              allowClear: false,
              placeholder: '選択してください',
              disabled: isMenuDisabled,
            }}
            controller={{ name: 'menu_id', control }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          />
          <ButtonCustom
            className="btn-default btn-chestnut-rose br-60 mt-30"
            loading={formState.isSubmitting}
            htmlType="submit"
            name="問診へ進む"
            showIconRight
            disabled={!canProceed}
          />
        </Form>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    background-color: var(--color-pampas);
    border: 1px solid var(--color-pampas);
    border-radius: 10px;
  }

  .ant-select-selection-placeholder {
    display: flex;
    align-items: center;
  }

  .request {
    border: 1px solid var(--color-mandys-pink);
    color: var(--color-white);
    background-color: var(--color-mandys-pink);
    padding: 3px 7px 0;
    font-size: 11px;
    font-weight: 700;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-align-items: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
  }
`
export default MedicalSpecialtyForm
